import { Divider, FlexProps } from "@mantine/core"
import "./index.css"
import { ScrollContainer } from "../scroll-container"
import {
  useSelectionState,
  IItemSelectionState,
} from "../../utils/useSelection"
import { stableStringify } from "../../utils/objectsAreEqual"

export interface RenderItemProps<T, TSelectable> {
  entity: T
  index: number
  selectionState: TSelectable extends false ? undefined : IItemSelectionState<T>
  containerRef: React.RefObject<HTMLDivElement>
}

export interface DefaultListProps<T, TSelectable extends boolean | undefined>
  extends FlexProps {
  data: T[]
  onSelectionChange?: (
    selection: ReturnType<typeof useSelectionState<T>>
  ) => void
  renderItem: (props: RenderItemProps<T, TSelectable>) => React.ReactNode
  itemRenderCondition?: (entity: T, index: number) => React.ReactNode
  divider?:
    | boolean
    | ((prev: T | undefined, curr: T | undefined) => React.ReactNode)
  scrollToBottom?: boolean
  startAtBottom?: boolean
  multiSelect?: boolean
  selectable?: TSelectable
}

export function DefaultList<T, TSelectable extends boolean | undefined>({
  data,
  renderItem,
  itemRenderCondition,
  divider,
  scrollToBottom,
  startAtBottom,
  onSelectionChange,
  children,
  multiSelect,
  selectable,
  ...props
}: DefaultListProps<T, TSelectable>) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const lastScrollDistanceRef = React.useRef<number | null>(null)

  const selectionState = useSelectionState(data, multiSelect, onSelectionChange)

  if (scrollToBottom && !startAtBottom && containerRef.current) {
    containerRef.current.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "smooth",
    })
  }

  const isReverse = props.direction === "column-reverse"

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const saveScroll = () => {
      lastScrollDistanceRef.current = isReverse
        ? el.scrollTop
        : el.scrollHeight - el.scrollTop
    }

    el.addEventListener("scroll", saveScroll)
  }, [isReverse])

  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    if (lastScrollDistanceRef.current != null) {
      const top = isReverse
        ? lastScrollDistanceRef.current
        : el.scrollHeight - lastScrollDistanceRef.current
      el.scrollTo({
        top,
      })
    }
  }, [data.length, isReverse])

  return (
    <ScrollContainer
      direction="column"
      ref={containerRef}
      mah="100%"
      w="100%"
      style={{ overflow: "auto" }}
      display="flex"
      {...props}
    >
      {data.map((entity, i, entries) => {
        const isReverse = props.direction === "column-reverse"
        const prevIndex = isReverse ? i + 1 : i - 1
        const prevItem = entries[prevIndex]

        if (itemRenderCondition && !itemRenderCondition(entity, i)) {
          return null
        }

        return (
          <React.Fragment
            key={
              i
              // stableStringify(entity)
            }
          >
            {!isReverse &&
              divider &&
              (typeof divider === "function"
                ? divider(prevItem, entity)
                : i > 0 && <Divider />)}

            {renderItem({
              entity,
              index: i,
              selectionState: selectable
                ? ({
                    ...selectionState,
                    isSelected: selectionState.isSelected(entity),
                  } as any)
                : undefined,
              containerRef,
            })}

            {isReverse &&
              divider &&
              (typeof divider === "function" ? (
                divider(prevItem, entity)
              ) : i < entries.length - 1 ? (
                <Divider key={stableStringify(entity) + "divider"} />
              ) : null)}
          </React.Fragment>
        )
      })}

      {children}
    </ScrollContainer>
  )
}
